import { Info } from '@mui/icons-material';
import { Chip } from '@mui/material';
import { GridValidRowModel } from '@mui/x-data-grid-pro';
import { Fragment, ReactNode, useState, useMemo } from 'react';
import { Union } from 'ts-toolbelt';

import { NULLISH_CELL_VALUE } from '../../DataGridCustom.types';
import { NBSP } from '~/common/constants';
import { InfoDrawer } from '~/components/InfoDrawer/InfoDrawer';
import { TooltipTypography } from '~/components/TooltipTypography/TooltipTypography';

export type ChipInfoCellValue = Union.Nullable<string | number>;

interface ChipInfoDetailsInfo {
  headline: ReactNode;
  subHeadline: ReactNode;
  description: ReactNode;
}

interface ChipInfoDetails {
  label: ReactNode;
  info?: ChipInfoDetailsInfo | null;
}

interface ChipInfoCellState {
  open: boolean;
  info?: ChipInfoDetailsInfo | null;
}

export type ChipInfoDetailsGetter<R> = (value: ChipInfoCellValue, row: R) => ChipInfoDetails;

interface ChipInfoCellProps<R extends GridValidRowModel = any> {
  value: ChipInfoCellValue | ChipInfoCellValue[];
  row: R;
  detailsGetter: ChipInfoDetailsGetter<R>;
}

export const ChipInfoCell = <R extends GridValidRowModel = any>({
  value: valueProp,
  row,
  detailsGetter,
}: ChipInfoCellProps<R>) => {
  const [state, setState] = useState<ChipInfoCellState>({ open: false });
  const values: ChipInfoCellProps<R>['value'] = useMemo(() => {
    if (valueProp) {
      return Array.isArray(valueProp) ? valueProp : [valueProp];
    }
    return null;
  }, [valueProp]);
  if (values) {
    const items = values
      .map((value, index: number) => {
        if (value) {
          const details = detailsGetter(value, row);
          const iconProps = details.info && {
            deleteIcon: <Info />,
            onDelete: () => {
              setState({ open: true, info: details.info });
            },
          };
          return (
            <Fragment key={index}>
              {index > 0 ? NBSP : null}
              <TooltipTypography key={index} title={details.label}>
                <Chip label={details.label} {...iconProps} />
              </TooltipTypography>
            </Fragment>
          );
        }
        return null;
      })
      .filter(Boolean);

    if (items.length) {
      return (
        <>
          {items}
          <InfoDrawer
            {...state.info}
            open={state.open}
            onClose={() => {
              setState((prev) => ({ ...prev, open: false }));
            }}
          />
        </>
      );
    }
  }
  return <>{NULLISH_CELL_VALUE}</>;
};
